import { useState, useEffect, createContext, useContext } from 'react';
import enTranslations from '../i18n/en.json';
import frTranslations from '../i18n/fr.json';

// Translations object
const translations = {
  en: enTranslations,
  fr: frTranslations
};

// Create translation context
const TranslationContext = createContext({
  language: 'en',
  t: (key, params) => key,
  setLanguage: () => {},
  isLoading: false
});

// Translation Provider Component
export function TranslationProvider({ children }) {
  const [language, setLanguageState] = useState('en'); // Default to English
  const [isLoading, setIsLoading] = useState(false);

  // Initialize language from localStorage, IP geolocation, or browser preference
  useEffect(() => {
    const initializeLanguage = async () => {
      // Check if user has manually set a language preference
      const userPreference = localStorage.getItem('kioo_lang_manual');
      const savedLang = localStorage.getItem('kioo_lang');
      
      if (userPreference === 'true' && savedLang && (savedLang === 'en' || savedLang === 'fr')) {
        // User has manually chosen a language - respect their choice
        setLanguageState(savedLang);
        return;
      }
      
      // Try IP-based geolocation to detect country
      try {
        setIsLoading(true);
        
        // Using ipapi.co free service (1000 requests/day, no API key needed)
        const response = await fetch('https://ipapi.co/json/', {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code?.toUpperCase();
          
          // List of French-speaking countries in West Africa and target regions
          const frenchSpeakingCountries = [
            'GN', // Guinea
            'CI', // Côte d'Ivoire
            'BF', // Burkina Faso
            'ML', // Mali
            'SN', // Senegal
            'TG', // Togo
            'BJ', // Benin
            'NE', // Niger
            'FR', // France
            'BE', // Belgium
            'CH', // Switzerland
            'CA', // Canada
            'MA', // Morocco
            'TN', // Tunisia
            'DZ', // Algeria
            'CM', // Cameroon
            'CD', // DR Congo
            'CG', // Republic of Congo
            'GA', // Gabon
            'TD', // Chad
            'CF', // Central African Republic
            'MG', // Madagascar
            'RE', // Réunion
            'MQ', // Martinique
            'GP', // Guadeloupe
            'GF', // French Guiana
            'PF', // French Polynesia
            'NC', // New Caledonia
            'HT'  // Haiti
          ];
          
          // Auto-switch to French for French-speaking countries
          if (frenchSpeakingCountries.includes(countryCode)) {
            setLanguageState('fr');
            localStorage.setItem('kioo_lang', 'fr');
            console.log(`Auto-detected French-speaking country: ${countryCode}`);
            setIsLoading(false);
            return;
          } else {
            // Default to English for all other countries
            setLanguageState('en');
            localStorage.setItem('kioo_lang', 'en');
            console.log(`Auto-detected country: ${countryCode}, using English`);
            setIsLoading(false);
            return;
          }
        }
      } catch (error) {
        console.log('IP geolocation failed, using fallback:', error);
      }
      
      // Fallback: Check browser language if IP detection fails
      const nav = (navigator.language || 'en').toLowerCase();
      const browserLang = nav.startsWith('fr') ? 'fr' : 'en';
      setLanguageState(browserLang);
      localStorage.setItem('kioo_lang', browserLang);
      setIsLoading(false);
    };

    initializeLanguage();
  }, []);

  // Handle language changes
  const setLanguage = (newLanguage) => {
    if (newLanguage === language || (newLanguage !== 'en' && newLanguage !== 'fr')) {
      return;
    }

    setLanguageState(newLanguage);
    localStorage.setItem('kioo_lang', newLanguage);
    // Mark that user has manually set their language preference
    localStorage.setItem('kioo_lang_manual', 'true');
    
    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('languageChanged', { 
      detail: { language: newLanguage } 
    }));
  };

  // Translation function with nested key support
  const t = (key, params = {}) => {
    if (!key || typeof key !== 'string') {
      return key || '';
    }

    // Handle nested keys like "nav.home" or "dashboard.weatherForecast"
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found in current language
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return the key itself if not found
          }
        }
        break;
      }
    }

    if (typeof value !== 'string') {
      return key;
    }

    // Simple parameter replacement
    let result = value;
    if (params && typeof params === 'object') {
      Object.keys(params).forEach(param => {
        const placeholder = `{${param}}`;
        result = result.replace(new RegExp(placeholder, 'g'), params[param]);
      });
    }

    return result;
  };

  // Format numbers according to locale
  const formatNumber = (number, options = {}) => {
    const locale = language === 'fr' ? 'fr-FR' : 'en-US';
    return new Intl.NumberFormat(locale, options).format(number);
  };

  // Format dates according to locale  
  const formatDate = (date, options = {}) => {
    const locale = language === 'fr' ? 'fr-FR' : 'en-US';
    return new Intl.DateTimeFormat(locale, options).format(new Date(date));
  };

  const contextValue = {
    language,
    setLanguage,
    t,
    formatNumber,
    formatDate,
    isLoading,
    isEnglish: language === 'en',
    isFrench: language === 'fr',
    switchLanguage: setLanguage, // Alias for compatibility
    getCurrentLanguage: () => language
  };

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
}

// Hook to use translation context
export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    // Fallback for when component is not wrapped in provider
    console.warn('useTranslation used outside TranslationProvider, using fallback');
    
    const [language, setLanguage] = useState(() => {
      const saved = localStorage.getItem('kioo_lang');
      return (saved === 'fr') ? 'fr' : 'en';
    });

    const t = (key, params = {}) => {
      if (!key || typeof key !== 'string') return key || '';
      
      const keys = key.split('.');
      let value = translations[language];
      
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          value = translations.en;
          for (const fallbackKey of keys) {
            if (value && typeof value === 'object' && fallbackKey in value) {
              value = value[fallbackKey];
            } else {
              return key;
            }
          }
          break;
        }
      }
      
      if (typeof value !== 'string') return key;
      
      let result = value;
      if (params && typeof params === 'object') {
        Object.keys(params).forEach(param => {
          result = result.replace(new RegExp(`{${param}}`, 'g'), params[param]);
        });
      }
      
      return result;
    };

    return {
      language,
      setLanguage: (newLang) => {
        setLanguage(newLang);
        localStorage.setItem('kioo_lang', newLang);
        localStorage.setItem('kioo_lang_manual', 'true');
      },
      t,
      isEnglish: language === 'en',
      isFrench: language === 'fr',
      isLoading: false
    };
  }
  return context;
}

// Component for translated text
export function T({ k, params = {}, children, fallback }) {
  const { t } = useTranslation();
  
  if (children && typeof children === 'function') {
    return children(t(k, params));
  }
  
  const translated = t(k, params);
  
  // If translation is the same as key and fallback provided, use fallback
  if (translated === k && fallback) {
    return fallback;
  }
  
  return translated;
}

// Higher-order component for translated components
export function withTranslation(Component) {
  return function TranslatedComponent(props) {
    const translation = useTranslation();
    return <Component {...props} {...translation} />;
  };
}

// Compatibility functions for legacy code
export const switchLanguage = (lang) => {
  localStorage.setItem('kioo_lang', lang);
  window.dispatchEvent(new CustomEvent('languageChanged', { 
    detail: { language: lang } 
  }));
  window.location.reload();
};

export const getCurrentLanguage = () => {
  return localStorage.getItem('kioo_lang') || 'en';
};

export const t = (key, params = {}) => {
  const lang = getCurrentLanguage();
  const keys = key.split('.');
  let value = translations[lang];
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      value = translations.en;
      for (const fallbackKey of keys) {
        if (value && typeof value === 'object' && fallbackKey in value) {
          value = value[fallbackKey];
        } else {
          return key;
        }
      }
      break;
    }
  }
  
  if (typeof value !== 'string') return key;
  
  let result = value;
  if (params && typeof params === 'object') {
    Object.keys(params).forEach(param => {
      result = result.replace(new RegExp(`{${param}}`, 'g'), params[param]);
    });
  }
  
  return result;
};